// Decoders of the sim's HUD views (crates/sim/src/view/hud.rs): "WMH5" (the HUD's state and the
// commentary channel), "WMR1" (the crafting recipes), "WMP1" (the weapons panel's build data) and
// "WMK1" (the crafting menu's CraftingMan answers for one team). Plain TypeScript with erasable
// syntax only (node loads it in client/tests).

/** CommentaryMan input since the channel's start: library comments (AddComment with the text
 * library entry guCommText[comment] + (choice + 1)), priority comments (AddPriorityComment) and
 * the crate collection lines. */
export type HudComment =
  | { seq: number; kind: 'library'; comment: number; choice: number; flush: boolean; worm: number; team: number; vehicle: number }
  | { seq: number; kind: 'priority'; text: string; delay: number; immediate: boolean; arg: string }
  | { seq: number; kind: 'collected'; equip: number; quantity: number; ingredient: boolean; team: number };

export type HudDamage = { done: boolean; pos: [number, number, number]; startY: number; colour: [number, number, number, number]; text: string };

export type HudTeam = {
  alive: boolean; alliance: number; ammo: Uint8Array; delay: Uint8Array; ingredients: number[];
  slots: { index: number; equip: number; finished: boolean }[];
  task: { type: number; turns: number; product: number };
  stats: { damage: number; kills: number; friendlyKills: number; deaths: number; wins: number; shots: number; crates: number };
  /** BaseRoundStats: the round results' kills, deaths, damage dealt and rank (0 first,
   * 0xffffffff before CalculateTeamRankings). */
  round: { kills: number; deaths: number; damage: number; rank: number };
  /** The round results' award (StatsMan::GetTeamAward; 0xffffffff none). */
  award: number;
};

export type HudView = {
  tick: number; nextComment: number; comments: HudComment[]; damages: HudDamage[]; teams: HudTeam[];
  craftingEnabled: boolean; craftingInTurn: boolean;
  awards: { stat: number; team: number; value: number }[];
  /** UICallback_MainGame_OnIdle's inputs: FLOW_* bits. */
  flow: number;
  /** BaseTurnStats::GetSeconds and BaseRoundStats::GetSeconds. */
  turnSecs: number; roundSecs: number;
  /** MatchSpec turn and hot seat times (seconds); the round clock is infinite. */
  turnTime: number; hotSeatTime: number; infiniteRound: boolean;
  /** The turn's InactiveTime (seconds without worm input). */
  inactive: number;
  /** BaseRoundStats::GetVictoriousAlliance (0xffffffff none) and m_NonLogicalGameEnded. */
  victorious: number; roundEnded: boolean;
  /** The inputs of WormHUDPanel::UpdateTransitionState's vehicle block in the current worm's
   * panel (hud.rs PanelVehicle) and that worm's GraphicUpdate visibility bits (ENTER_*). */
  enter: HudEnter;
};

/** hud.rs `PanelVehicle`: `runs` counts the hook's runs (unchanged: the block did not run);
 * `worm` 0xffffffff: no current worm; `ran`: its panel's LogicUpdate ran; `vehicle` =
 * Worm::GetVehicleToEnter (0xffffffff none); `found`: VehicleMan::GetVehicle has it, then its
 * m_CachedFlags, HUDEnterOffset, GetPosition (y up) and VehicleEnum id; `fuel`: the current
 * worm's GadgetParams m_fJetPackFuel where the panels read it (-1: none); `select`:
 * WormHUDSelect::IsArrowActive at the end of WormHUDMan::LogicUpdate; `gx`, `gy`: the current
 * worm's Worm::GetGraphicalPosition there (the turn arrow's SetPosition). */
export type HudEnter = {
  runs: number; worm: number; ran: boolean; vehicle: number; team: number; teamAI: boolean;
  found: boolean; cached: number; offset: number; x: number; y: number; id: number; visible: number; fuel: number; select: boolean;
  gx: number; gy: number;
};

/** HudEnter.visible bits: IsLabelVisible, the task's logical state bit 0, IsDead, HasSurrendered,
 * IsDrowning, jet packing (GadgetParams m_fJetPackFuel != -1). */
export const ENTER_LABEL = 1, ENTER_ACTIVE = 2, ENTER_DEAD = 4, ENTER_SURRENDERED = 8, ENTER_DROWNING = 16, ENTER_JETPACK = 32;

/** One fully observed sim step's HUD inputs: the WMH5 view and the frame view's fields the HUD
 * state machine reads (the worker keeps every step's, so the HUD steps tick by tick); `wind` =
 * BaseTurnStats::GetWindSpeed(false) (UpdateWindArrows' input); `maxHealth` = BaseRoundStats::
 * GetMaximumTeamHealth (each team's running maximum); `phase`: the frame view's phase (Wait
 * ExitMatch = NUI_Cache::m_bShowRoundResults, which BaseRoundLogic::Update_WaitSaveGame sets with
 * Goodbye1); `craft`: the WMK1 view of the current team (the weapons panel's team), on the steps
 * the worker presents (none between turns or in a fast-forward); `restored`: the first step after
 * a snapshot restore, whose earlier ticks the HUD never saw (the HUD synchronises from its state). */
export type HudStep = {
  hud: Uint8Array; turnFlags: number; currentWorm: number; currentTeam: number; health: number[]; maxHealth: number[]; wind: number; phase: number;
  craft?: Uint8Array; restored?: boolean;
};

/** `CraftingMan`'s answers to the crafting menu of one team (WMK1): `crafting` = IsTeamCrafting ||
 * IsTeamDismantling (the menu's cancel layout), `cancel` = CanCancel, `craft[e]` /
 * `dismantle[e]` = CanCraftWeapon / CanDismantleWeapon of equip e (error bits, 0 = accepted;
 * 0xffff unknown). */
export type CraftChecks = { tick: number; team: number; crafting: boolean; cancel: number; craft: Uint16Array; dismantle: Uint16Array };

export const FLOW_RETREAT_REQUESTED = 1, FLOW_END_OF_HOTSEAT = 2, FLOW_DAMAGE_APPLIED = 4, FLOW_TURN_DONE = 8,
  FLOW_ROUND_DONE = 16, FLOW_TELEPORTING = 32, FLOW_ANY_INPUT = 64;

export type Recipe = { product: number; parent: number; ingredients: number[]; salvage: number[]; variations: number[]; known: number };

export type PanelInfo = { script: string; equip: { icon: number; delayed: number; noAmmo: number; flags: number; name: string }[] };

const td = new TextDecoder('latin1');

class R {
  dv: DataView; o = 0; b: Uint8Array;
  constructor(b: Uint8Array) { this.b = b; this.dv = new DataView(b.buffer, b.byteOffset, b.byteLength); }
  u8(): number { return this.dv.getUint8(this.o++); }
  u16(): number { const v = this.dv.getUint16(this.o, true); this.o += 2; return v; }
  u32(): number { const v = this.dv.getUint32(this.o, true); this.o += 4; return v; }
  f32(): number { const v = this.dv.getFloat32(this.o, true); this.o += 4; return v; }
  bytes(n: number): Uint8Array { const v = this.b.subarray(this.o, this.o + n); this.o += n; return v; }
  str(): string { return td.decode(this.bytes(this.u16())); }
  magic(m: string): void {
    const got = td.decode(this.bytes(4));
    if (got !== m) throw new Error(`hud view: magic ${got}, expected ${m}`);
  }
}

export function decodeHud(b: Uint8Array): HudView {
  const r = new R(b);
  r.magic('WMH5');
  const tick = r.u32();
  const nextComment = r.u32();
  const comments: HudComment[] = [];
  for (let n = r.u8(); n > 0; n--) {
    const seq = r.u32(), kind = r.u8();
    if (kind === 0) comments.push({ seq, kind: 'library', comment: r.u8(), choice: r.u8(), flush: !!r.u8(), worm: r.u32(), team: r.u32(), vehicle: r.u32() });
    else if (kind === 1) comments.push({ seq, kind: 'priority', text: r.str(), delay: r.f32(), immediate: !!r.u8(), arg: r.str() });
    else comments.push({ seq, kind: 'collected', equip: r.u32(), quantity: r.u32(), ingredient: !!r.u8(), team: r.u32() });
  }
  const damages: HudDamage[] = [];
  for (let n = r.u8(); n > 0; n--) {
    const done = !!r.u8();
    const pos: [number, number, number] = [r.f32(), r.f32(), r.f32()];
    const startY = r.f32();
    const colour: [number, number, number, number] = [r.f32(), r.f32(), r.f32(), r.f32()];
    damages.push({ done, pos, startY, colour, text: r.str() });
  }
  const teams: HudTeam[] = [];
  for (let n = r.u8(); n > 0; n--) {
    const alive = !!r.u8(), alliance = r.u32();
    const ammo = r.bytes(134).slice(), delay = r.bytes(134).slice();
    const ingredients = [r.u32(), r.u32(), r.u32(), r.u32()];
    const slots = [];
    for (let k = r.u8(); k > 0; k--) slots.push({ index: r.u8(), equip: r.u16(), finished: !!r.u8() });
    const task = { type: r.u8(), turns: r.u32(), product: r.u16() };
    const stats = { damage: r.u32(), kills: r.u32(), friendlyKills: r.u32(), deaths: r.u32(), wins: r.u32(), shots: r.u32(), crates: r.u32() };
    const round = { kills: r.u32(), deaths: r.u32(), damage: r.u32(), rank: r.u32() };
    teams.push({ alive, alliance, ammo, delay, ingredients, slots, task, stats, round, award: r.u32() });
  }
  const craftingEnabled = !!r.u8(), craftingInTurn = !!r.u8();
  const awards = [];
  for (let n = r.u8(); n > 0; n--) awards.push({ stat: r.u32(), team: r.u32(), value: r.u32() });
  const flow = r.u8();
  const turnSecs = r.f32(), roundSecs = r.f32(), turnTime = r.u8(), hotSeatTime = r.u8(), infiniteRound = !!r.u8(), inactive = r.f32();
  const victorious = r.u32(), roundEnded = !!r.u8();
  const enter: HudEnter = {
    runs: r.u32(), worm: r.u32(), ran: !!r.u8(), vehicle: r.u32(), team: r.u32(), teamAI: !!r.u8(), found: !!r.u8(),
    cached: r.u32(), offset: r.f32(), x: r.f32(), y: r.f32(), id: r.u32(), visible: r.u8(), fuel: r.f32(), select: !!r.u8(), gx: r.f32(), gy: r.f32(),
  };
  return { tick, nextComment, comments, damages, teams, craftingEnabled, craftingInTurn, awards, flow, turnSecs, roundSecs, turnTime, hotSeatTime, infiniteRound, inactive, victorious, roundEnded, enter };
}

export function decodeRecipes(b: Uint8Array): Recipe[] {
  const r = new R(b);
  r.magic('WMR1');
  const out: Recipe[] = [];
  for (let n = r.u8(); n > 0; n--) {
    const product = r.u16(), parent = r.u16();
    const ingredients = [r.u32(), r.u32(), r.u32(), r.u32()];
    const salvage = [r.u32(), r.u32(), r.u32(), r.u32()];
    const variations = [];
    for (let k = r.u8(); k > 0; k--) variations.push(r.u16());
    out.push({ product, parent, ingredients, salvage, variations, known: r.u8() });
  }
  return out;
}

export function decodePanel(b: Uint8Array): PanelInfo {
  const r = new R(b);
  r.magic('WMP1');
  const script = td.decode(r.bytes(r.u32()));
  const equip = [];
  for (let n = 0; n < 134; n++) equip.push({ icon: r.u16(), delayed: r.u16(), noAmmo: r.u16(), flags: r.u32(), name: r.str() });
  return { script, equip };
}

/** The WMK1 view; null when the sim had no answer (no CraftingMan, no such team). */
export function decodeCraft(b: Uint8Array): CraftChecks | null {
  const r = new R(b);
  r.magic('WMK1');
  const tick = r.u32(), team = r.u8();
  if (!r.u8()) return null;
  const crafting = !!r.u8(), cancel = r.u16();
  const craft = new Uint16Array(134), dismantle = new Uint16Array(134);
  for (let e = 0; e < 134; e++) {
    craft[e] = r.u16();
    dismantle[e] = r.u16();
  }
  return { tick, team, crafting, cancel, craft, dismantle };
}
